import { useMemo } from "react"
import { Autocomplete, CircularProgress, TextField } from "@mui/material"
import { Controller, useFormContext } from "react-hook-form"
import { useTranslation } from "react-i18next"

import type { Powerline } from "types/powerlines.types"

import { useAllPowerlines } from "helpers/hooks/powerlines/useAllPowerlines"
import { nameOrUnnamed } from "helpers/utils/translations"

interface PowerlineAutocompleteProps {
  name: string
  placeholder?: string
  initialData?: Powerline
}

const PowerlineAutocomplete = ({
  name,
  placeholder,
  initialData,
}: PowerlineAutocompleteProps) => {
  const { t } = useTranslation()
  const { control } = useFormContext()
  const { powerlines, isLoading } = useAllPowerlines()

  const options = useMemo(
    () =>
      powerlines?.map((p) => ({
        id: p.id,
        label: `${nameOrUnnamed(p.name, t)} (${p.id})`,
      })) || [],
    [powerlines, t],
  )

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        const { onChange, value, ref } = field

        const selectedOption = (() => {
          if (!value) return null
          const found = options.find((opt) => opt.id === value)
          if (found) return found
          if (initialData && initialData.id === value) {
            return {
              id: initialData.id,
              label: `${nameOrUnnamed(initialData.name, t)} (${initialData.id})`,
            }
          }
          return { id: value as number, label: String(value) }
        })()

        const optionsWithInitial =
          initialData &&
          initialData.id === value &&
          !options.some((opt) => opt.id === initialData.id)
            ? [
                {
                  id: initialData.id,
                  label: `${nameOrUnnamed(initialData.name, t)} (${initialData.id})`,
                },
                ...options,
              ]
            : options

        return (
          <Autocomplete
            options={optionsWithInitial}
            loading={isLoading}
            value={selectedOption}
            onChange={(_event, newValue) => {
              onChange(newValue ? newValue.id : null)
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => option.label || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={ref}
                variant="outlined"
                size="small"
                placeholder={placeholder}
                error={!!error}
                helperText={error?.message}
                sx={{ mb: 0 }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )
      }}
    />
  )
}

export default PowerlineAutocomplete
